import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import { config } from '@/config'

// ============================================================================
// Types
// ============================================================================

export interface ApiError {
  message: string
  errors?: Record<string, string[]>
  status: number
  code?: string
}

export interface ApiResponse<T = unknown> {
  data: T
  message?: string
  success: boolean
}

export interface PaginatedResponse<T> {
  data: T[]
  meta: PaginationMeta
  links?: PaginationLinks
}

export interface PaginationMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
  from?: number
  to?: number
}

export interface PaginationLinks {
  first?: string
  last?: string
  prev?: string
  next?: string
}

// ============================================================================
// Error Handling
// ============================================================================

class ApiClientError extends Error {
  public status: number
  public errors?: Record<string, string[]>
  public code?: string
  public isNetworkError: boolean

  constructor(
    message: string,
    status: number,
    errors?: Record<string, string[]>,
    code?: string,
    isNetworkError = false
  ) {
    super(message)
    this.name = 'ApiClientError'
    this.status = status
    this.errors = errors
    this.code = code
    this.isNetworkError = isNetworkError
  }
}

// Error messages by status code
const ERROR_MESSAGES: Record<number, string> = {
  400: 'Bad request. Please check your input.',
  401: 'Session expired. Please log in again.',
  403: 'You do not have permission to access this resource.',
  404: 'The requested resource was not found.',
  408: 'Request timeout. Please try again.',
  422: 'Validation error. Please check your input.',
  429: 'Too many requests. Please wait a moment.',
  500: 'Server error. Please try again later.',
  502: 'Bad gateway. Please try again later.',
  503: 'Service unavailable. Please try again later.',
  504: 'Gateway timeout. Please try again later.',
}

// ============================================================================
// Token Management
// ============================================================================

const TOKEN_KEY = 'auth_token'
const REFRESH_TOKEN_KEY = 'refresh_token'

export const tokenManager = {
  getToken: (): string | null => {
    if (typeof window === 'undefined') return null
    return localStorage.getItem(TOKEN_KEY)
  },

  setToken: (token: string): void => {
    if (typeof window === 'undefined') return
    localStorage.setItem(TOKEN_KEY, token)
  },

  getRefreshToken: (): string | null => {
    if (typeof window === 'undefined') return null
    return localStorage.getItem(REFRESH_TOKEN_KEY)
  },

  setRefreshToken: (token: string): void => {
    if (typeof window === 'undefined') return
    localStorage.setItem(REFRESH_TOKEN_KEY, token)
  },

  clearTokens: (): void => {
    if (typeof window === 'undefined') return
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  },

  isAuthenticated: (): boolean => {
    return !!tokenManager.getToken()
  },
}

// ============================================================================
// Create API Client
// ============================================================================

function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: config.api.baseUrl,
    timeout: config.api.timeout,
    headers: {
      Accept: 'application/json',
    },
    withCredentials: false,
  })

  // Request Interceptor
  client.interceptors.request.use(
  (axiosConfig: InternalAxiosRequestConfig) => {
    const token = tokenManager.getToken()

    if (token && axiosConfig.headers) {
      axiosConfig.headers.Authorization = `Bearer ${token}`
    }

    // Only set Content-Type for requests with bodies (POST, PUT, PATCH)
    // GET/DELETE requests shouldn't have Content-Type to avoid CORS preflight
    const hasBody = axiosConfig.data !== undefined && axiosConfig.data !== null
    const method = axiosConfig.method?.toUpperCase()
    if (hasBody && ['POST', 'PUT', 'PATCH'].includes(method || '')) {
      if (axiosConfig.headers) {
        axiosConfig.headers['Content-Type'] = 'application/json'
      }
    }


    return axiosConfig
  },
  () => {
    return Promise.reject(
      new ApiClientError('Request configuration error', 0, undefined, 'REQUEST_ERROR')
    )
  }
  )

  // Response Interceptor
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      // Normalize successful response
      return response
    },
    async (error: AxiosError<ApiError>) => {
      // Handle network errors
      if (!error.response) {
        if (error.code === 'ECONNABORTED') {
          throw new ApiClientError(
            'Request timeout. Please check your connection.',
            408,
            undefined,
            'TIMEOUT',
            false
          )
        }

        // Detect CORS errors
        const isCorsError = 
          error.message?.includes('CORS') ||
          error.message?.includes('Network Error') ||
          (error.code === 'ERR_NETWORK' && typeof window !== 'undefined')

        if (isCorsError) {
          throw new ApiClientError(
            'CORS error: Unable to connect to API. Please check server configuration.',
            0,
            undefined,
            'CORS_ERROR',
            true
          )
        }

        throw new ApiClientError(
          'Network error. Please check your internet connection.',
          0,
          undefined,
          'NETWORK_ERROR',
          true
        )
      }

      const { status, data } = error.response

      // Handle 401 - Unauthorized
      if (status === 401) {
        tokenManager.clearTokens()

        // Redirect to login if not already there
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
          window.location.href = '/login?expired=true'
        }
      }

      // Build error message
      const message = data?.message || ERROR_MESSAGES[status] || 'An unexpected error occurred'

      throw new ApiClientError(message, status, data?.errors, error.code)
    }
  )

  return client
}

// ============================================================================
// API Client Instance
// ============================================================================

export const apiClient = createApiClient()

// ============================================================================
// Helper Functions
// ============================================================================

export function getErrorMessage(error: unknown): string {
  if (error instanceof ApiClientError) {
    return error.message
  }

  if (axios.isAxiosError(error)) {
    return error.response?.data?.message || error.message || 'An error occurred'
  }

  if (error instanceof Error) {
    return error.message
  }

  return 'An unknown error occurred'
}

export function getValidationErrors(error: unknown): Record<string, string[]> | undefined {
  if (error instanceof ApiClientError) {
    return error.errors
  }

  if (axios.isAxiosError(error)) {
    return error.response?.data?.errors
  }

  return undefined
}

export function isNetworkError(error: unknown): boolean {
  if (error instanceof ApiClientError) {
    return error.isNetworkError
  }

  if (axios.isAxiosError(error)) {
    return !error.response
  }

  return false
}

export function isUnauthorizedError(error: unknown): boolean {
  if (error instanceof ApiClientError) {
    return error.status === 401
  }

  if (axios.isAxiosError(error)) {
    return error.response?.status === 401
  }

  return false
}

// ============================================================================
// Request Helpers
// ============================================================================

export async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const response = await apiClient.get<T>(url, { params })
  return response.data
}

export async function post<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.post<T>(url, data)
  return response.data
}

export async function put<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.put<T>(url, data)
  return response.data
}

export async function patch<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.patch<T>(url, data)
  return response.data
}

export async function del<T>(url: string): Promise<T> {
  const response = await apiClient.delete<T>(url)
  return response.data
}

export default apiClient

